import { SetStateAction, useEffect, useState } from "react";
import moment from "moment";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Form, Spinner, Table } from "react-bootstrap";
import FormInput from "../../components/FormInput";
import searchIcon from "../../assets/icons/search.svg";
import PaginationComponent from "../../components/PaginationComponent";


interface WalletReport {
	_id: string,
	walletId: string,
	reference: string,
	product: string,
	type: string,
	description: string,
	amount: number,
	charge: number,
	previousBalance: number,
	newBalance: number,
	user: string,
	createdAt: string,
}


const WalletsReports = () => {

	const [walletsReports, setWalletsReports] = useState<WalletReport[]>([]);
	const [selectedType, setSelectedType] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({
		currentPage: 1,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		nextPage: null,
		previousPage: null
	});

	const handlePaginate = (pageNumber: number) => {
		setPaginationData((prevData) => ({ ...prevData, currentPage: pageNumber }));
	};

	const formatDate = (dateString: moment.MomentInput) => {
		return moment(dateString).format("DD-MM-YYYY / hh:mmA");
	};

	const formatAmount = (price: number | bigint) => {
		return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 }).format(price);
	};

	const handleStartDateChange = (e: { target: { value: SetStateAction<string>; }; }) => {
		setStartDate(e.target.value);
	};

	const handleEndDateChange = (e: { target: { value: SetStateAction<string>; }; }) => {
		setEndDate(e.target.value);
	};

	const fetchWalletsReports = async () => {
		setIsLoading(true);
		try {
			api.setAuthorization(api.getLoggedInUser());
			const queryParams = new URLSearchParams({ page: paginationData.currentPage?.toString() || '1' });
			if (selectedType) {
				queryParams.set('type', selectedType);
			};
			if (startDate) {
				queryParams.set('startDate', moment(startDate).format('YYYY-MM-DD'));
			}
			if (endDate) {
				queryParams.set('endDate', moment(endDate).format('YYYY-MM-DD'));
			}

			const response = await api.get(endpoints.getAllWalletsReports, queryParams);
			setWalletsReports(response.data.data.docs);
			setPaginationData({
				currentPage: response.data.data.page,
				totalPages: response.data.data.totalPages,
				hasNextPage: response.data.data.hasNextPage,
				hasPreviousPage: response.data.data.hasPreviousPage,
				nextPage: response.data.data.nextPage,
				previousPage: response.data.data.previousPage
			});
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching transactions:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchWalletsReports();
	}, [paginationData.currentPage, selectedType, startDate, endDate]);

	const getTypeClass = (type: string | undefined) => {
		let typeClass = '';
		if (type === 'credit') {
			typeClass = 'wallet-inflow';
		} else if (type === 'debit') {
			typeClass = 'wallet-outflow';
		}
		return typeClass;
	};


	return (
		<>
			<div className="table-topbar">
				<div className="search_bar">
					<img src={searchIcon} alt="search" />
					<FormInput name="search" placeholder="Search" />
				</div>
				<div className="select-type mt-4">
					<Form.Select
						name="type"
						title="type"
						onChange={e => setSelectedType((e.target as HTMLSelectElement).value)}
					>
						<option value="">Type</option>
						<option value="credit">Credit</option>
						<option value="debit">Debit</option>
					</Form.Select>
				</div>
				<div className="range-input-section mb-3 mt-2">
					<FormInput
						label="From:"
						type="date"
						name="startDate"
						value={startDate}
						onChange={handleStartDateChange}
					/>
					<FormInput
						label="To:"
						type="date"
						name="endDate"
						value={endDate}
						onChange={handleEndDateChange}
					/>
				</div>
			</div>
			<div className="table-body">
				<Table>
					{isLoading ? (
						<div className="spinner-div">
							<Spinner animation="border" size="sm" role="status" />
						</div>
					) : walletsReports.length > 0 ? (
						<>
							<thead>
								<tr>
									<th className="custom-thead-bg">Product</th>
									<th className="custom-thead-bg">Type</th>
									<th className="custom-thead-bg">Description</th>
									<th className="custom-thead-bg">Amount</th>
									<th className="custom-thead-bg">User Name</th>
									<th className="custom-thead-bg">Previous Balance</th>
									<th className="custom-thead-bg">New Balance</th>
									<th className="custom-thead-bg">Date</th>
								</tr>
							</thead>
							<tbody>
								{walletsReports.map((walletsReport, index) => (
									<tr key={walletsReport._id}>
										<td>{walletsReport.product}</td>
										<td className={getTypeClass(walletsReport.type)}>{walletsReport.type}</td>
										<td>{walletsReport.description}</td>
										<td>{formatAmount(walletsReport.amount)}</td>
										<td>{walletsReport.user}</td>
										<td>{formatAmount(walletsReport.previousBalance)}</td>
										<td className="wallet-balance">{formatAmount(walletsReport.newBalance)}</td>
										<td>{formatDate(walletsReport.createdAt)}</td>
									</tr>
								))}
							</tbody>
						</>
					) : (
						<div className="no-results">
							No activities found.
						</div>
					)}
				</Table>
			</div>

			<PaginationComponent
				currentPage={paginationData.currentPage}
				totalPages={paginationData.totalPages}
				hasNextPage={paginationData.hasNextPage}
				hasPreviousPage={paginationData.hasPreviousPage}
				nextPage={paginationData.nextPage}
				previousPage={paginationData.previousPage}
				onPaginate={handlePaginate}
			/>
		</>
	)
};

export default WalletsReports;